// Shadcn Components
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";

// Common Components
import { DisabledFeatureMessage } from "@/components/common/DisabledFeatureMessage";

// Hooks
import { useAuthenticationChoice } from '../../hooks/authentication/useAuthenticationChoice';
import { useOrganizationOnboardingFlag } from '../../hooks/common/useFeatureFlag';

const AuthenticationChoice = () => {
  const {
    orgId,
    setOrgId,
    error,
    setError,
    handleJoinOrg,
    handleCreateOrg
  } = useAuthenticationChoice();

  const { isEnabled: organizationOnboardingEnabled, loading } = useOrganizationOnboardingFlag();

  const gradientStyle = {
    background: "linear-gradient(45deg, #f804b4 0%, #08ccc4 100%)"
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center px-4 py-12" 
      style={gradientStyle}
    >
      <div className="flex flex-col md:flex-row w-[90%] sm:w-[85%] md:w-[75%] lg:w-[65%] xl:w-[55%] max-w-3xl rounded-lg overflow-hidden bg-white shadow-2xl">
        
        {/* Left panel - Create Organization */}
        <div className="w-full md:w-1/2 bg-white p-6 md:p-12">
          <div className="w-full max-w-[280px] mx-auto flex flex-col h-full">
            <div className="flex flex-col items-center">
              <div className="bg-white">
                <img 
                  src="/branding/TriEvents_Loading_Big_Text.gif" 
                  alt="TriEvents Logo" 
                  className="w-40 sm:w-48 h-auto"
                />
              </div>
              <p className="text-muted-foreground text-center text-sm mt-2 mb-6">
                Create your organization and start managing events
              </p>
            </div>
            {loading ? (
              <div className="mt-4 md:mt-auto w-full max-w-[280px] text-center text-muted-foreground">
                Loading...
              </div>
            ) : organizationOnboardingEnabled ? (
              <Button 
                onClick={handleCreateOrg}
                className="mt-4 md:mt-auto w-full max-w-[280px] transition-transform hover:scale-105"
                size="lg"
              >
                Create Organization
              </Button>
            ) : (
              <div className="mt-4 md:mt-auto w-full max-w-[280px]">
                <DisabledFeatureMessage 
                  featureName="Organization creation"
                />
              </div>
            )}
          </div>
        </div>

        {/* Right panel - Join Organization */}
        <div className="w-full md:w-1/2 bg-white p-6 md:p-12">
          <div className="w-full max-w-[280px] mx-auto flex flex-col h-full">
            <div className="flex flex-col items-center">
              <h1 className="text-2xl font-semibold text-foreground mb-3 text-center">
                Join or Login
              </h1>
              <p className="text-muted-foreground text-center text-sm mt-0.5 mb-6">
                Already have an organization? Enter your organization ID to continue
              </p>
              
              <form onSubmit={handleJoinOrg} className="w-full">
                <div className="w-full mb-6 relative">
                  <Label className="text-sm font-medium mb-2 block" htmlFor="orgId">
                    Organization ID
                  </Label>
                  <Input
                    className={cn(
                      "w-full",
                      error && "border-destructive focus-visible:ring-destructive"
                    )}
                    placeholder="Enter your organization ID"
                    type="text"
                    id="orgId"
                    value={orgId}
                    onChange={(e) => {
                      setOrgId(e.target.value);
                      setError('');
                    }}
                    required
                  />
                </div>
                {error && (
                  <Alert variant="destructive" className="mb-6">
                    <AlertDescription>
                      {error}
                    </AlertDescription>
                  </Alert>
                )}
                <Button 
                  type="submit"
                  className="w-full max-w-[280px] transition-transform hover:scale-105"
                  size="lg"
                >
                  Continue to Organization
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthenticationChoice;
